import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/api";
import { dict } from "@/i18n";
import useLiveSync from "@/lib/useLiveSync";

const AppCtx = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  useLiveSync(!!user);
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "en");
  const [loading, setLoading] = useState(true);
  const [shops, setShops] = useState([]);
  const [shopBump, setShopBump] = useState(0); // increment to force refreshes across the app

  const t = useCallback((k) => (dict[lang] && dict[lang][k]) || dict.en[k] || k, [lang]);

  const loadShops = useCallback(async () => {
    try { const r = await api.get("/shops"); setShops(r.data); }
    catch (e) { console.error("Failed to load shops:", e?.response?.status, e?.response?.data || e?.message); }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me").then(async (r) => {
      setUser(r.data);
      if (r.data.language) { setLang(r.data.language); localStorage.setItem("lang", r.data.language); }
      await loadShops();
    }).catch(() => localStorage.removeItem("token")).finally(() => setLoading(false));
  }, [loadShops]);

  const afterAuth = async (data) => {
    localStorage.setItem("token", data.token);
    setUser(data.user);
    if (data.user.language) { setLang(data.user.language); localStorage.setItem("lang", data.user.language); }
    await loadShops();
  };

  const login = async (identifier, password) => {
    const r = await api.post("/auth/login", { identifier, password });
    await afterAuth(r.data);
    return r.data.user;
  };

  const loginWithOtp = async (identifier, code) => {
    const r = await api.post("/auth/otp/verify", { identifier, code });
    await afterAuth(r.data);
    return r.data.user;
  };

  const register = async (payload) => {
    const r = await api.post("/auth/register", payload);
    await afterAuth(r.data);
    return r.data.user;
  };

  const logout = () => { localStorage.removeItem("token"); setUser(null); setShops([]); };

  const changeLang = async (l) => {
    setLang(l); localStorage.setItem("lang", l);
    if (user) { try { await api.patch("/auth/language", { language: l }); } catch (e) { console.error("Failed to persist language:", e?.message); } }
  };

  const switchShop = async (sid) => {
    await api.post(`/shops/${sid}/switch`);
    // Reload so every page fetches data scoped to the new shop.
    window.location.assign("/");
  };

  const addShop = async (name) => {
    const r = await api.post("/shops", { name });
    await loadShops();
    return r.data;
  };

  const deleteShop = async (sid) => {
    await api.delete(`/shops/${sid}`);
    const me = await api.get("/auth/me");
    setUser(me.data);
    await loadShops();
    setShopBump((n) => n + 1);
  };

  const activeShop = shops.find((s) => s.id === user?.active_shop_id) || null;

  return (
    <AppCtx.Provider value={{
      user, setUser, lang, setLang: changeLang, t, login, loginWithOtp, register, logout, loading,
      shops, activeShop, switchShop, addShop, deleteShop, loadShops, shopBump,
    }}>
      {children}
    </AppCtx.Provider>
  );
}

export const useApp = () => useContext(AppCtx);
